import {
  FOOTER_RAIL_GAP_PX,
  FOOTER_RAIL_HYSTERESIS_PX,
  FOOTER_RAIL_TILE_ESTIMATE_PX,
  type FooterRailId,
} from './footer-rail.constants';

export interface SplitFooterRailArgs {
  /** Visible collapsible ids in priority order. */
  orderedIds: readonly FooterRailId[];
  pinnedIds: readonly FooterRailId[];
  /** Partial; missing ids use the estimate. */
  heights: ReadonlyMap<FooterRailId, number>;
  moreHeight: number;
  /** Viewport-keyed budget; independent of what the collapsible set contains. */
  availableHeight: number;
  /** Everything not collapsed: chrome + primary + pinned footer. */
  reservedHeight: number;
  /** Previous pass's overflow state; drives the hysteresis. */
  wasOverflowing: boolean;
}

export interface SplitFooterRailResult {
  railIds: FooterRailId[];
  moreIds: FooterRailId[];
}

/**
 * Decides which footer items stay visible and which collapse into the More
 * (surface-agnostic). Anti-oscillation is the CALLER's job: it must feed
 * viewport-keyed heights, never the post-collapse footer height — a content-sized
 * footer doesn't get that for free like the flex-1 rail.
 */
export function splitFooterRail({
  orderedIds,
  pinnedIds,
  heights,
  moreHeight,
  availableHeight,
  reservedHeight,
  wasOverflowing,
}: SplitFooterRailArgs): SplitFooterRailResult {
  const heightOf = (id: FooterRailId) => heights.get(id) ?? FOOTER_RAIL_TILE_ESTIMATE_PX;

  const stackHeight = (ids: readonly FooterRailId[]) =>
    ids.reduce(
      (sum, id, index) => sum + heightOf(id) + (index ? FOOTER_RAIL_GAP_PX : 0),
      0
    );

  const budget = availableHeight - reservedHeight;

  // Everything fits. Once More is showing, require a little extra room before
  // dropping it so a one-pixel drag can't strobe the tile.
  const fitThreshold = wasOverflowing ? budget - FOOTER_RAIL_HYSTERESIS_PX : budget;
  if (stackHeight(orderedIds) <= fitThreshold) {
    return { railIds: [...orderedIds], moreIds: [] };
  }

  const pinned = new Set(pinnedIds);

  let used = moreHeight;
  const rail: FooterRailId[] = [];

  const take = (id: FooterRailId) => {
    used += heightOf(id) + FOOTER_RAIL_GAP_PX;
    rail.push(id);
  };

  for (const id of orderedIds) {
    if (pinned.has(id)) take(id);
  }

  // Fill by priority, stop at the first that doesn't fit — no skip-ahead, so a
  // shorter low-priority item can't leapfrog a taller higher-priority one.
  for (const id of orderedIds) {
    if (pinned.has(id)) continue;
    if (used + heightOf(id) + FOOTER_RAIL_GAP_PX > budget) break;
    take(id);
  }

  rail.sort((a, b) => orderedIds.indexOf(a) - orderedIds.indexOf(b));
  const moreIds = orderedIds.filter((id) => !rail.includes(id));

  // Hiding a single item behind More costs a row and saves a row. Put it back.
  if (moreIds.length === 1 && stackHeight(orderedIds) <= budget + moreHeight) {
    return { railIds: [...orderedIds], moreIds: [] };
  }

  return { railIds: rail, moreIds };
}
